import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().min(1, "Phone number is required"),
  email: z.string().email("Invalid email address"),
  reason: z.string().min(1, "Please provide a reason for your visit"),
})

type FormData = z.infer<typeof formSchema>

export function ConsultationForm() {
  const [isLoading, setIsLoading] = useState(false)
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      reason: "",
    },
  })

  const onSubmit = async (data: FormData) => {
    setIsLoading(true)
    try {
      console.log("Submitting consultation request:", data)
      
      // First, save to contact_submissions table
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert([{
          name: data.name,
          email: data.email,
          message: `Phone: ${data.phone}\nReason: ${data.reason}`,
        }])

      if (dbError) {
        console.error('Database submission error:', dbError)
        throw new Error('Failed to save consultation request')
      }

      // Then send SMS notification
      const { error: smsError } = await supabase.functions.invoke('send-consultation-sms', {
        body: { data }
      })

      if (smsError) {
        console.error('SMS notification error:', smsError)
        // Don't throw here since the submission was saved
        toast.error("Consultation request saved but notification failed to send.")
      } else {
        toast.success("Consultation request sent successfully! We'll contact you soon.")
      }

      form.reset()
    } catch (error) {
      console.error('Error processing consultation request:', error)
      toast.error("Failed to send consultation request. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input placeholder="Your phone number" type="tel" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Your email" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason for Visit</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Please describe what services you're interested in"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Sending..." : "Schedule Consultation"}
        </Button>
      </form>
    </Form>
  )
}